import { Container, Typography, Box, Divider, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from 'swr';
import { IOrderItem } from "../../../models/IWallet";
import { orderDetailstyle } from "../../../styles/ProtectedPage/ordersDetails";
import { getOrderById } from "../../../api/OrderAPI";
import { typo } from "../../../assets/fonts/font";
import colorConfigs from "../../../configs/colorConfigs";
import { calculateCouponSummary } from "./orderDetailsUtils";

const formatCurrency = (amount: number | undefined) => `$ ${(amount ?? 0).toFixed(2)}`;

const formatPaymentAmount = (amount: number | undefined) => `$ ${(amount ?? 0).toFixed(2)}`;

const OrderDetailsPage = () => {
  const { orderId } = useParams<{ orderId?: string }>();
  const navigate = useNavigate();

  const { data: orderDetails, error: orderError } = useSWR(
    orderId ? ['getOrderById', orderId] : null,
    () => orderId ? getOrderById(orderId) : null
  );

  if (orderError) return <div>Error loading order details</div>;
  if (!orderDetails) return <div>Loading order details...</div>;

  const {
    subtotal = 0,
    items = [],
    payments = [],
    coupon_usages = []
  } = orderDetails;

  const couponSummary = calculateCouponSummary(subtotal, coupon_usages);

  return (
    <Container sx={orderDetailstyle.container}>
      <Box sx={{ width: '100%', maxWidth: '600px', display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/member/orders')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{...typo.sectionHeadline, flex: 1, textAlign: 'center', mb: 0}}>
          Order Details
        </Typography>
      </Box>
      <Typography sx={{ ...typo.bodyContent}}>
        Order Date:{' '}
        { orderDetails?.created_at ? new Date(orderDetails.created_at).toLocaleString() : '-' }
      </Typography>
      <Box sx={orderDetailstyle.summaryBox}>
        <Box sx={orderDetailstyle.headerRow}>
          <Typography sx={{...typo.bodyContent, ...orderDetailstyle.headerText, color: colorConfigs.primary}}>
            Product
          </Typography>
          <Typography sx={{...typo.bodyContent, ...orderDetailstyle.headerText, color: colorConfigs.primary}}>
            Total
          </Typography>
        </Box>

        {items.length > 0 ? (
          items.map((item: IOrderItem) => (
            <Box key={item.sku_id} sx={orderDetailstyle.itemRow}>
              <Box sx={orderDetailstyle.itemDetails}>
                <Typography sx={{...typo.bodyContent, ...orderDetailstyle.itemPrice}}>
                  {item.sku_name} × {item.quantity}
                </Typography>
              </Box>
              <Typography sx={{...typo.bodyContent, ...orderDetailstyle.itemPrice}}>
                {formatCurrency(item.item_total)}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography sx={{...typo.bodyContent, ...orderDetailstyle.noItemsText}}>No items available for this order.</Typography>
        )}

        <Divider sx={orderDetailstyle.divider} />

        <Box sx={orderDetailstyle.subtotalRow}>
          <Typography sx={{...typo.bodyContent, ...orderDetailstyle.subtotalText}}>Subtotal:</Typography>
          <Typography sx={{...typo.bodyContent, ...orderDetailstyle.subtotalText}}>{formatCurrency(subtotal)}</Typography>
        </Box>

        {couponSummary.items.length > 0 ? (
          <>
            <Box sx={orderDetailstyle.discountSection}>
              <Typography sx={{...typo.bodyContent, ...orderDetailstyle.discountTitle}}>Coupon usage:</Typography>
              {couponSummary.items.map((coupon, index) => (
                <Box key={`${coupon.name}-${index}`} sx={orderDetailstyle.discountRow}>
                  <Typography sx={{...typo.bodyContent, ...orderDetailstyle.itemPrice}}>
                    Applied voucher: {coupon.name}
                  </Typography>
                  <Typography sx={{...typo.bodyContent, ...orderDetailstyle.itemPrice}}>
                    {formatCurrency(coupon.deductedAmount)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={orderDetailstyle.summaryRow}>
              <Typography sx={{...typo.bodyContent, ...orderDetailstyle.summaryLabel}}>Deducted amount:</Typography>
              <Typography sx={{...typo.bodyContent, ...orderDetailstyle.summaryValue}}>{formatCurrency(couponSummary.totalDiscount)}</Typography>
            </Box>

            <Box sx={orderDetailstyle.summaryRow}>
              <Typography sx={{...typo.bodyContent, ...orderDetailstyle.summaryLabel}}>Total after discount:</Typography>
              <Typography sx={{...typo.bodyContent, ...orderDetailstyle.summaryValue}}>{formatCurrency(couponSummary.totalAfterDiscount)}</Typography>
            </Box>
          </>
        ) : (
          <Box sx={orderDetailstyle.summaryRow}>
            <Typography sx={{...typo.bodyContent, ...orderDetailstyle.summaryLabel}}>Total:</Typography>
            <Typography sx={{...typo.bodyContent, ...orderDetailstyle.summaryValue}}>{formatCurrency(subtotal)}</Typography>
          </Box>
        )}

        <Divider sx={orderDetailstyle.divider} />

        <Box sx={orderDetailstyle.paymentRow}>
          <Box sx={orderDetailstyle.paymentLabelColumn}>
            <Typography sx={{...typo.bodyContent, ...orderDetailstyle.paymentLabel}}>Payment method:</Typography>
            {payments.length > 0 ? (
              payments.map((payment: { type: string; amount: number }, index: number) => (
                <Box key={`${payment.type}-${index}`} sx={orderDetailstyle.paymentSubRow}>
                  <Typography sx={{...typo.bodyContent, ...orderDetailstyle.paymentSubLabel}}>
                    {payment.type}
                  </Typography>
                  <Typography sx={{...typo.bodyContent, ...orderDetailstyle.paymentText}}>
                    {formatPaymentAmount(payment.amount)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography sx={{...typo.bodyContent, ...orderDetailstyle.paymentSubLabel}}>N/A</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default OrderDetailsPage;
